import React from 'react';
import './App.css';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useParams,
} from 'react-router-dom';
import Navbar from './components/navigation/navbar';
import MapGrid from './components/grids/mapGrid';
import TestGrid from './components/grids/testGrid';
import OrgGrid from './components/grids/orgGrid';
import OrgScoring from './components/grids/orgScoring';
import OrgLocation from './components/grids/orgLocation';
import PublicLeaderboard, { PublicCarDetail } from './components/grids/publicScoring';
import StaticHtmlPage from './components/grids/staticHtmlPage';

// The same SPA build is served at both `go-ev.net` and `org.go-ev.net`. The
// subdomain case flattens the org tools to the root (`/`, `/scoring`,
// `/location`) so links read as `org.go-ev.net/scoring` instead of
// `org.go-ev.net/org/scoring`. We decide which shape to use by inspecting
// the hostname at load time.
const isOrgHost =
  typeof window !== 'undefined' &&
  window.location.hostname.split('.')[0] === 'org';

// Full-page redirect used on the main domain to push legacy `/org/*` links
// over to the dedicated subdomain. Uses the page's current protocol so it
// works equally on plain http:// and on https:// once TLS is in place.
const ExternalRedirect: React.FC<{ host: string; path: string }> = ({
  host,
  path,
}) => {
  const loc = useLocation();
  React.useEffect(() => {
    const scheme = window.location.protocol; // "http:" or "https:"
    window.location.replace(
      `${scheme}//${host}${path}${loc.search}${loc.hash}`,
    );
  }, [host, path, loc.search, loc.hash]);
  return null;
};

const DashboardRedirect: React.FC = () => {
  const { CarNumber } = useParams();
  return <Navigate to={CarNumber ? `/${CarNumber}` : '/'} replace />;
};

const App: React.FC = () => {
  return (
    <Router>
      <Navbar />
      <Routes>
        {isOrgHost ? (
          <>
            <Route path="/" element={<OrgGrid />} />
            <Route path="/scoring" element={<OrgScoring />} />
            <Route path="/location" element={<OrgLocation />} />
            {/* If someone types the old /org/* path on the subdomain, fix it */}
            <Route path="/org" element={<Navigate to="/" replace />} />
            <Route path="/org/scoring" element={<Navigate to="/scoring" replace />} />
            <Route path="/org/location" element={<Navigate to="/location" replace />} />
            {/* Anything else on the subdomain falls back to the Live Board */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </>
        ) : (
          <>
            <Route path="/" element={<PublicLeaderboard />} />
            <Route path="/map" element={<MapGrid />} />
            <Route
              path="/results"
              element={
                <StaticHtmlPage
                  src="/results.html"
                  title="2026 Endurance Results"
                />
              }
            />
            <Route
              path="/comparison"
              element={
                <StaticHtmlPage
                  src="/comparison.html"
                  title="2026 Endurance Scoring - FH Monitor vs Official"
                />
              }
            />
            <Route path="/dashboard" element={<DashboardRedirect />} />
            <Route path="/dashboard/:CarNumber" element={<DashboardRedirect />} />
            <Route path="/test" element={<TestGrid />} />
            {/* Legacy /org/* → org.go-ev.net/* (scheme follows the current page) */}
            <Route
              path="/org"
              element={<ExternalRedirect host="org.go-ev.net" path="/" />}
            />
            <Route
              path="/org/scoring"
              element={<ExternalRedirect host="org.go-ev.net" path="/scoring" />}
            />
            <Route
              path="/org/location"
              element={<ExternalRedirect host="org.go-ev.net" path="/location" />}
            />
            <Route path="/:CarNumber" element={<PublicCarDetail />} />
          </>
        )}
      </Routes>
    </Router>
  );
};

export default App;
